/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { ThemeProvider } from 'styled-components';
import { StyledEngineProvider } from '@mui/material/styles';
import createGenerateClassName from '@mui/styles/createGenerateClassName';
import jssPreset from '@mui/styles/jssPreset';
import StylesProvider from '@mui/styles/StylesProvider';
import { create } from 'jss';
import { Store } from 'redux';
import appTheme from './theme/appTheme.json';
import { SnackbarProvider } from 'notistack';
import SecurityProvider from './SecurityProvider';

// This is needed so that Material UI only uses one css classname generator.
// See for more details: https://material-ui.com/getting-started/faq/#why-aren-39-t-my-components-rendering-correctly-in-production-builds
const generateClassName = createGenerateClassName({
  productionPrefix: 'c',
});

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById('jss-insertion-point') as HTMLElement,
});

interface AppProvidersProps {
  store: Store;
  history: History;
  children: JSX.Element[] | JSX.Element;
}

const AppProviders: React.StatelessComponent<AppProvidersProps> = (
  props: AppProvidersProps,
): React.ReactComponentElement<'div', {}> => {
  const { store, history, children } = props;

  return (
    <StylesProvider jss={jss} generateClassName={generateClassName}>
      <Provider store={store}>
        <ConnectedRouter history={history}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={appTheme}>
              <SecurityProvider>
                <SnackbarProvider
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  preventDuplicate
                  autoHideDuration={null}
                  iconVariant={{ error: null }}
                  maxSnack={4}
                >
                  {children}
                </SnackbarProvider>
              </SecurityProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </ConnectedRouter>
      </Provider>
    </StylesProvider>
  );
};

export default AppProviders;
